<template>
  <!-- + Properties ListMap -->
  <div class="container-fluid" data-file="PropertiesSearchMainMap">
    <!-- + Mobile Filter only -->
    <div
      v-show="!mapIsOpen"
      data-component="sorting-and-map-button"
      :class="[
        'row tw-my-[10px] tw-items-center tw-justify-center',
        { 'tw-flex md:tw-hidden': !mapIsOpen },
      ]"
    >
      <div
        class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-[16px] md:tw-hidden"
      >
        <!-- Grid / Split toggle (mobile) — only from 1200px; hidden below that -->
        <div
          v-if="canShowGridSplitMaps"
          class="tw-hidden tw-h-[44px] tw-flex-1 tw-items-center tw-justify-center min-[1200px]:tw-flex"
          data-testid="grid-split-view-toggle-mobile"
        >
          <div
            class="tw-flex tw-w-full tw-rounded-[16px] tw-bg-white tw-shadow-[0_1px_4.7px_0_rgba(0,0,0,0.05)]"
            role="tablist"
            aria-label="Search view"
          >
            <button
              type="button"
              role="tab"
              :aria-selected="isGridView"
              data-testid="grid-view-tab-mobile"
              :class="[
                'tw-flex tw-h-[44px] tw-flex-1 tw-items-center tw-justify-center tw-gap-1 tw-rounded-l-[16px] tw-px-4 tw-py-2 tw-text-[14px] tw-font-semibold tw-leading-[21px] focus-visible:tw-z-10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2',
                isGridView
                  ? 'tw-bg-[#00628F] tw-text-white'
                  : 'tw-bg-transparent tw-text-[#3B4143]',
              ]"
              @click="setMarketplaceView('grid_view')"
            >
              <!-- Phosphor SquaresFour (fill) -->
              <svg
                class="tw-h-[18px] tw-w-[18px] tw-shrink-0"
                viewBox="0 0 256 256"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M120,56v48a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V56A16,16,0,0,1,56,40h48A16,16,0,0,1,120,56Zm80-16H152a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V56A16,16,0,0,0,200,40Zm-96,96H56a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V152A16,16,0,0,0,104,136Zm96,0H152a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V152A16,16,0,0,0,200,136Z"
                />
              </svg>
              Grid
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="isSplitView"
              data-testid="split-view-tab-mobile"
              :class="[
                'tw-flex tw-h-[44px] tw-flex-1 tw-items-center tw-justify-center tw-gap-1 tw-rounded-r-[16px] tw-px-4 tw-py-2 tw-text-[14px] tw-font-semibold tw-leading-[21px] focus-visible:tw-z-10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2',
                isSplitView
                  ? 'tw-bg-[#00628F] tw-text-white'
                  : 'tw-bg-transparent tw-text-[#3B4143]',
              ]"
              @click="setMarketplaceView('split_view')"
            >
              <!-- Phosphor SplitHorizontal (regular) -->
              <svg
                class="tw-h-[18px] tw-w-[18px] tw-shrink-0"
                viewBox="0 0 256 256"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M112,48V208a8,8,0,0,1-16,0V136H43.31l18.35,18.34a8,8,0,0,1-11.32,11.32l-32-32a8,8,0,0,1,0-11.32l32-32a8,8,0,0,1,11.32,11.32L43.31,120H96V48a8,8,0,0,1,16,0Zm125.66,74.34-32-32a8,8,0,0,0-11.32,11.32L212.69,120H160V48a8,8,0,0,0-16,0V208a8,8,0,0,0,16,0V136h52.69l-18.35,18.34a8,8,0,0,0,11.32,11.32l32-32A8,8,0,0,0,237.66,122.34Z"
                />
              </svg>
              Split
            </button>
          </div>
        </div>

        <!-- Map Button -->
        <button
          v-else-if="
            propertiesData.length &&
            canShowMapView &&
            mapViewPillPositionTest !== 'bottom_fixed'
          "
          id="show_map"
          ref="showMap"
          data-testid="show-new-map"
          class="tw-flex tw-h-[40px] tw-flex-1 tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-[31px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-text-[14px] tw-leading-[32px] tw-text-[#5C6A75] hover:tw-text-[#5C6A75] focus:tw-text-[#5C6A75]"
          title="Show the map"
          type="button"
          @click="toggleMap"
        >
          <i class="fas view-on-map font-size-20px tw-pr-[4px] tw-pt-[4px]" />
          <span
            class="d-md-block tw-text-[14px] tw-font-semibold tw-leading-[21px] tw-text-primary-grey"
          >
            <span class="hidden min-[405px]:inline-flex pr-[10px]">
              {{ mapIsOpen ? 'Close map' : 'Map view' }}
            </span>
            <span class="inline-flex min-[405px]:hidden pr-[10px]">
              {{ mapIsOpen ? 'Close map' : 'Map view' }}
            </span>
          </span>
        </button>

        <!-- Sort / Property Type Button -->
        <PropertyTypeFiltersTrigger
          v-if="enablePropertyTypeFilters"
          :expanded="propertyTypeFiltersExpanded"
          class="tw-w-[188px] tw-shrink-0 md:tw-hidden"
          @toggle="propertyTypeFiltersExpanded = !propertyTypeFiltersExpanded"
        />
        <div
          v-else
          class="custom-select-wrapper-bw tw-flex tw-flex-1 md:tw-hidden"
          data-uh="sort-by"
          data-label="properties-search-main-backup-2024-0"
        >
          <div class="custom-select noSelect tw-w-full tw-border-none">
            <!-- Selected option -->
            <div
              :class="[
                'tw-flex tw-h-[44px] tw-w-full tw-cursor-pointer tw-touch-manipulation tw-flex-row tw-content-evenly tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-[100px] tw-border-[1px] tw-border-[#D1D5DB] tw-bg-white !tw-px-[10px] tw-text-[16px] tw-leading-[32px] tw-text-[#5C6A75] hover:tw-text-black focus:tw-text-black',
                { disabled: reloading },
              ]"
            >
              <div class="arrow-off">
                <i
                  class="fas sort-by-search font-size-20px sort tw-ml-1 tw-pt-[7px]"
                ></i>
              </div>
              <span
                class="d-md-none tw-inline-block tw-max-w-[120px] tw-overflow-hidden tw-truncate tw-text-ellipsis tw-whitespace-nowrap tw-text-[14px] tw-font-semibold tw-leading-[21px] tw-text-primary-grey"
                :title="currentSortTitleShort"
              >
                {{ currentSortTitleShort }}
              </span>
            </div>
            <div
              class="custom-options tw-z-20 tw-rounded-[16px] tw-p-0 tw-drop-shadow-md"
              :class="{
                'tw-left-[-200px] md:tw-left-[-120px]':
                  mapViewPillPositionTest !== 'bottom_fixed',
              }"
              data-uh="sort-by-results"
            >
              <span
                v-for="(value, key, index) in sortOptions"
                :key="index"
                class="custom-option transition-all duration-300 ease-in-out tw-m-0 tw-px-[24px] tw-py-[16px]"
                :class="{
                  selected: currentSort === key,
                  'tw-border-t-[1px] tw-border-[#E5E7EB]': index !== 0,
                }"
                :data-uh="`sort-by-${key}`"
                @click="$emit('set-current-sort', key)"
              >
                {{ value.full }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PropertyTypeFiltersExtension
      v-if="enablePropertyTypeFilters"
      class="tw-mb-5 md:tw-hidden"
      :active="currentType"
      :expanded="propertyTypeFiltersExpanded"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      :show-mobile-trigger="false"
      @update:expanded="propertyTypeFiltersExpanded = $event"
      @change="$emit('set-current-type', $event)"
    />
    <!-- - Sorting and Map Button Mobile -->

    <!--  Student accommodation info  -->
    <div v-show="!mapIsOpen" class="row tw-justify-center">
      <div
        class="container-fluid contained tw-mx-0 tw-flex tw-max-w-screen-xl tw-items-center tw-gap-3 tw-p-0"
      >
        <h2
          class="ml-0 mr-auto tw-line tw-font-md tw-text-trueGray-700 tw-mb-[12px] tw-mt-[12px] tw-text-[18px] tw-leading-[27px] md:tw-mb-0 md:tw-mt-[24px] md:tw-text-[26px] md:tw-leading-[38px]"
          dusk="student-accommodation-search-headline"
        >
          <template v-if="shortlistActive">
            {{ shortlistReadOnly ? 'Shared Shortlist' : shortlistTitle || 'My Shortlist' }}
          </template>
          <template v-else>
            Student accommodation
            <span v-if="currentCity">in</span>
            <span
              v-if="currentCity"
              class="tw-font-semibold"
              data-testid="in-current-city-name"
              >{{ `${currentCity.name}` }}</span
            >
          </template>
        </h2>
        <button
          v-if="shortlistActive && !shortlistReadOnly"
          type="button"
          class="tw-mt-[12px] tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-[#0082B8] md:tw-mt-[24px]"
          :disabled="sharingShortlist"
          dusk="share-shortlist"
          @click="shareShortlist"
        >
          <img
            :src="cdnAsset('svg/uh-share-blue-icon.svg')"
            alt=""
            class="tw-h-5 tw-w-5"
          />
          <span class="tw-hidden sm:tw-inline">{{ shareShortlistLabel }}</span>
        </button>
      </div>
    </div>
    <!-- - Student accommodation info  -->

    <!--    Sorting and Map Button Desktop -->
    <div
      v-show="!mapIsOpen"
      ref="mapButtonContainer"
      class="row tw-justify-center"
      :class="{ 'tw-hidden md:tw-block': !mapIsOpen }"
    >
      <div
        class="container-fluid contained tw-mb-5 tw-flex tw-max-w-screen-xl tw-justify-between tw-p-0"
      >
        <div class="tw-flex">
          <h3
            class="d-none d-md-block mb-0 ml-0 tw-min-w-30 tw-min-h-8 tw-w-30 tw-mr-2.5 tw-h-8 tw-self-baseline tw-text-[20px] tw-font-semibold tw-leading-[28px]"
            dusk="desktop-title-result-count"
          >
            {{ title }}
          </h3>

          <span
            class="tw-mt-[12px] tw-hidden tw-self-baseline tw-text-[14px] tw-text-sm tw-leading-[21px] tw-text-[#5C6A75] xl:tw-inline"
            >Use filters for room and price to find your perfect place.</span
          >
        </div>
        <div
          :class="
            canShowGridSplitMaps
              ? 'tw-flex tw-items-center tw-gap-3'
              : 'tw-flex'
          "
        >
          <template v-if="canShowGridSplitMaps">
            <!-- + Sorting (Grid/Split experience) -->
            <div class="display-flex mr-0 tw-mt-[6px] tw-hidden md:tw-block">
              <div
                class="custom-select-wrapper-bw"
                data-uh="sort-by"
                data-label="properties-search-main-backup-2024-1"
                dusk="desktop-sort-by"
              >
                <div class="border-none custom-select noSelect">
                  <div
                    :class="[
                      'align-items-center d-flex justify-content-center ml-auto mr-0 min-w-40px tw-cursor-pointer tw-border-transparent tw-px-[8px] tw-text-[16px] tw-leading-[32px] tw-text-[#5C6A75] hover:tw-text-black focus:tw-text-black',
                      {
                        disabled: reloading,
                      },
                    ]"
                  >
                    <span
                      class="d-md-none pr-10px font-weight-600 tw-text-[16px] tw-leading-[32px]"
                      >{{ currentSortTitleShort }}</span
                    >
                    <span
                      class="d-none d-md-block pr-10px font-weight-600 tw-text-[16px] tw-leading-[32px]"
                      >{{ currentSortTitle }}</span
                    >
                    <div class="arrow-off">
                      <i class="fas sort-by-search font-size-20px sort"></i>
                    </div>
                  </div>
                  <div
                    class="custom-options l-minus-120px tw-z-20 tw-rounded-[16px] tw-p-0 tw-drop-shadow-md"
                    data-uh="sort-by-results"
                    dusk="desktop-sort-by-results"
                  >
                    <span
                      v-for="(value, key, index) in sortOptions"
                      :key="index"
                      class="custom-option transition-all duration-300 ease-in-out tw-m-0 tw-px-[24px] tw-py-[16px]"
                      :class="{
                        selected: currentSort === key,
                        'tw-border-t-[1px] tw-border-[#E5E7EB]': index !== 0,
                      }"
                      :data-uh="`sort-by-${key}`"
                      :dusk="`sort-by-${key}`"
                      @click="$emit('set-current-sort', key)"
                    >
                      {{ value.full }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- - Sorting -->
            <!-- + Grid / Split toggle (desktop) — only from 1200px -->
            <div
              class="tw-mt-[6px] tw-hidden min-[1200px]:tw-block"
              data-testid="grid-split-view-toggle"
            >
              <div
                class="tw-flex tw-rounded-[16px] tw-bg-white tw-shadow-[0_1px_4.7px_0_rgba(0,0,0,0.05)]"
                role="tablist"
                aria-label="Search view"
              >
                <button
                  type="button"
                  role="tab"
                  :aria-selected="isGridView"
                  data-testid="grid-view-tab"
                  dusk="desktop-grid-view"
                  :class="[
                    'tw-flex tw-h-[44px] tw-items-center tw-justify-center tw-gap-1 tw-rounded-l-[16px] tw-px-4 tw-py-2 tw-text-[14px] tw-font-semibold tw-leading-[21px] focus-visible:tw-z-10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2',
                    isGridView
                      ? 'tw-bg-[#00628F] tw-text-white'
                      : 'tw-bg-transparent tw-text-[#3B4143]',
                  ]"
                  @click="setMarketplaceView('grid_view')"
                >
                  <!-- Phosphor SquaresFour (fill) -->
                  <svg
                    class="tw-h-[18px] tw-w-[18px] tw-shrink-0"
                    viewBox="0 0 256 256"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M120,56v48a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V56A16,16,0,0,1,56,40h48A16,16,0,0,1,120,56Zm80-16H152a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V56A16,16,0,0,0,200,40Zm-96,96H56a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V152A16,16,0,0,0,104,136Zm96,0H152a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V152A16,16,0,0,0,200,136Z"
                    />
                  </svg>
                  Grid
                </button>
                <button
                  type="button"
                  role="tab"
                  :aria-selected="isSplitView"
                  data-testid="split-view-tab"
                  dusk="desktop-split-view"
                  :class="[
                    'tw-flex tw-h-[44px] tw-items-center tw-justify-center tw-gap-1 tw-rounded-r-[16px] tw-px-4 tw-py-2 tw-text-[14px] tw-font-semibold tw-leading-[21px] focus-visible:tw-z-10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2',
                    isSplitView
                      ? 'tw-bg-[#00628F] tw-text-white'
                      : 'tw-bg-transparent tw-text-[#3B4143]',
                  ]"
                  @click="setMarketplaceView('split_view')"
                >
                  <!-- Phosphor SplitHorizontal (regular) -->
                  <svg
                    class="tw-h-[18px] tw-w-[18px] tw-shrink-0"
                    viewBox="0 0 256 256"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M112,48V208a8,8,0,0,1-16,0V136H43.31l18.35,18.34a8,8,0,0,1-11.32,11.32l-32-32a8,8,0,0,1,0-11.32l32-32a8,8,0,0,1,11.32,11.32L43.31,120H96V48a8,8,0,0,1,16,0Zm125.66,74.34-32-32a8,8,0,0,0-11.32,11.32L212.69,120H160V48a8,8,0,0,0-16,0V208a8,8,0,0,0,16,0V136h52.69l-18.35,18.34a8,8,0,0,0,11.32,11.32l32-32A8,8,0,0,0,237.66,122.34Z"
                    />
                  </svg>
                  Split
                </button>
              </div>
            </div>
            <!-- - Grid / Split toggle -->
          </template>
          <template v-else>
            <!-- + Views -->
            <div
              class="display-flex ml-auto mx-0 tw-mt-[6px] tw-hidden lg:tw-block"
            >
              <button
                v-if="
                  propertiesData.length &&
                  canShowMapView &&
                  mapViewPillPositionTest !== 'bottom_fixed'
                "
                id="show_map"
                ref="showMap"
                class="align-items-center d-flex justify-content-center ml-auto mr-0 p-10px min-w-40px tw-touch-manipulation tw-border-transparent tw-px-[8px] tw-py-0 tw-text-[16px] tw-leading-[32px] tw-text-[#5C6A75] hover:tw-text-black focus:tw-text-black"
                title="Show the map"
                type="button"
                dusk="desktop-show-map"
                @click.prevent="toggleMap"
              >
                <i class="fas view-on-map font-size-20px pr-10px" />
                <span
                  class="d-md-none pr-10px font-weight-600 tw-text-[16px] tw-leading-[32px]"
                  >{{ mapIsOpen ? 'Close' : 'Map' }}</span
                >
                <span
                  class="d-none d-md-block pr-10px font-weight-600 tw-text-[16px] tw-leading-[32px]"
                  >{{ mapIsOpen ? 'Close map' : 'View on map' }}</span
                >
              </button>
            </div>
            <!-- - Views -->
            <!-- + Sorting -->
            <div
              :class="[
                'display-flex mr-0 pl-10px tw-ml-auto tw-mt-[6px] tw-hidden md:tw-block',
              ]"
            >
              <div
                class="custom-select-wrapper-bw"
                data-uh="sort-by"
                data-label="properties-search-main-backup-2024-1"
                dusk="desktop-sort-by"
              >
                <div class="border-none custom-select noSelect">
                  <div
                    :class="[
                      'align-items-center d-flex justify-content-center ml-auto mr-0 min-w-40px tw-cursor-pointer tw-border-transparent tw-px-[8px] tw-text-[16px] tw-leading-[32px] tw-text-[#5C6A75] hover:tw-text-black focus:tw-text-black',
                      {
                        disabled: reloading,
                      },
                    ]"
                  >
                    <span
                      class="d-md-none pr-10px font-weight-600 tw-text-[16px] tw-leading-[32px]"
                      >{{ currentSortTitleShort }}</span
                    >
                    <span
                      class="d-none d-md-block pr-10px font-weight-600 tw-text-[16px] tw-leading-[32px]"
                      >{{ currentSortTitle }}</span
                    >
                    <div class="arrow-off">
                      <i class="fas sort-by-search font-size-20px sort"></i>
                    </div>
                  </div>
                  <div
                    class="custom-options l-minus-120px tw-z-20 tw-rounded-[16px] tw-p-0 tw-drop-shadow-md"
                    data-uh="sort-by-results"
                    dusk="desktop-sort-by-results"
                  >
                    <span
                      v-for="(value, key, index) in sortOptions"
                      :key="index"
                      class="custom-option transition-all duration-300 ease-in-out tw-m-0 tw-px-[24px] tw-py-[16px]"
                      :class="{
                        selected: currentSort === key,
                        'tw-border-t-[1px] tw-border-[#E5E7EB]': index !== 0,
                      }"
                      :data-uh="`sort-by-${key}`"
                      :dusk="`sort-by-${key}`"
                      @click="$emit('set-current-sort', key)"
                    >
                      {{ value.full }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- - Sorting -->
          </template>
        </div>
      </div>
    </div>
    <!-- - Sorting and Map Button Desktop -->

    <UpcomingPropertiesBanner
      v-if="displayUpcomingPropertiesBanner"
    ></UpcomingPropertiesBanner>

    <!-- + Map (full-page). Flag off, mobile, or an explicit view=map_view. -->
    <div
      v-if="
        !canShowGridSplitMaps || isMobile || selectedView === 'map_view'
      "
      class="row tw-justify-center"
      data-component="map-container"
    >
      <div
        ref="propertiesListMap"
        class="container-fluid tw-p-0"
        :class="{ contained: !mapIsOpen }"
      >
        <div class="row">
          <div
            v-if="mapIsOpen"
            class="tw-flex tw-h-[calc(100vh-64px)] tw-w-full tw-flex-col tw-bg-white"
          >
            <OpenMap
              :center="[currentCity.longitude, currentCity.latitude]"
              :enable-shortlist-in-search="enableShortlistInSearch"
              @shortlist-changed="handleMapShortlistChanged"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Map -->

    <!-- List / Grid / Split results -->
    <div
      v-show="!mapIsOpen"
      ref="listContainer"
      class="row tw-justify-center"
      data-component="list-container"
    >
      <div
        ref="propertiesListMap"
        class="container-fluid tw-max-w-screen-xl tw-p-0"
        :class="{
          contained: !mapIsOpen,
          'updated-property-tile-container':
            enableUpdatedPropertyTile && !isSideMapActive,
        }"
      >
        <div
          :class="
            isSideMapActive
              ? 'tw-flex tw-w-full tw-flex-col'
              : 'row'
          "
        >
          <div
            :class="
              isSideMapActive
                ? 'tw-grid tw-w-full tw-items-start tw-gap-6'
                : 'tw-contents'
            "
            :style="isSideMapActive ? gridSideMapRowStyle : null"
          >
          <!-- + List -->
          <div
            ref="propertiesList"
            class="properties_listing_container"
            :class="gridSideMapListPaneClass"
            :style="isSideMapActive ? gridSideMapListPaneStyle : null"
          >
            <div
              v-if="isMultiPropertyEnquiryEnabled && isEnquirySelectionMode"
              class="tw-mb-[24px] tw-flex tw-items-start tw-gap-2 tw-rounded-[16px] tw-bg-[#F5FDFF] tw-px-[26px] tw-py-[16px] tw-text-[16px] tw-leading-[24px] tw-text-[#6B7280]"
              :class="{ 'tw-col-span-full': isSideMapActive }"
              role="status"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19.5"
                height="19.5"
                viewBox="0 0 20 20"
                fill="none"
                class="tw-mt-0.5 tw-shrink-0"
              >
                <path
                  d="M9.75 0C7.82164 0 5.93657 0.571828 4.33319 1.64317C2.72982 2.71451 1.48013 4.23726 0.742179 6.01884C0.00422452 7.80042 -0.188858 9.76082 0.187348 11.6521C0.563554 13.5434 1.49215 15.2807 2.85571 16.6443C4.21928 18.0079 5.95656 18.9365 7.84787 19.3127C9.73919 19.6889 11.6996 19.4958 13.4812 18.7578C15.2627 18.0199 16.7855 16.7702 17.8568 15.1668C18.9282 13.5634 19.5 11.6784 19.5 9.75C19.4973 7.16498 18.4692 4.68661 16.6413 2.85872C14.8134 1.03084 12.335 0.00272983 9.75 0ZM9.375 4.5C9.59751 4.5 9.81501 4.56598 10 4.6896C10.185 4.81321 10.3292 4.98891 10.4144 5.19448C10.4995 5.40005 10.5218 5.62625 10.4784 5.84448C10.435 6.06271 10.3278 6.26316 10.1705 6.4205C10.0132 6.57783 9.81271 6.68498 9.59448 6.72838C9.37625 6.77179 9.15005 6.74951 8.94449 6.66436C8.73892 6.57922 8.56322 6.43502 8.4396 6.25002C8.31598 6.06501 8.25 5.8475 8.25 5.625C8.25 5.32663 8.36853 5.04048 8.57951 4.8295C8.79049 4.61853 9.07664 4.5 9.375 4.5ZM10.5 15C10.1022 15 9.72065 14.842 9.43934 14.5607C9.15804 14.2794 9 13.8978 9 13.5V9.75C8.80109 9.75 8.61033 9.67098 8.46967 9.53033C8.32902 9.38968 8.25 9.19891 8.25 9C8.25 8.80109 8.32902 8.61032 8.46967 8.46967C8.61033 8.32902 8.80109 8.25 9 8.25C9.39783 8.25 9.77936 8.40804 10.0607 8.68934C10.342 8.97064 10.5 9.35218 10.5 9.75V13.5C10.6989 13.5 10.8897 13.579 11.0303 13.7197C11.171 13.8603 11.25 14.0511 11.25 14.25C11.25 14.4489 11.171 14.6397 11.0303 14.7803C10.8897 14.921 10.6989 15 10.5 15Z"
                  fill="#00628F"
                />
              </svg>
              <span>
                Select properties to enquire about.
                <span class="tw-text-[#3B4143]">
                  Click anywhere on a property card to select it. Select at
                  least one to continue.
                </span>
              </span>
            </div>
            <!--
              Only mount when the nudge is visible. An empty wrapper inside the
              grid side-map pane (tw-gap-y-9) still creates a grid row + gap and
              pushes cards below the map top.
            -->
            <div
              v-if="
                !mapIsOpen &&
                showSpareRoomsOneBedNudge &&
                spareRoomsOneBedOverlayCount > 0
              "
              class="tw-min-w-0 tw-w-full"
              :class="{ 'tw-col-span-full': isSideMapActive }"
            >
              <SpareRoomsNudge
                :add-md-horizontal-padding="false"
                :count="spareRoomsOneBedOverlayCount"
                :apply-url="spareRoomsOneBedOverlayApplyUrl"
              />
            </div>
            <!--
              Grid side-map: tw-contents so cards participate in the parent
              subgrid (equal tracks + equal gaps with the map). Avoid Bootstrap
              row/col gutters which overflow the contained container.
            -->
            <div
              :class="
                isSideMapActive ? 'tw-contents' : 'position-relative row'
              "
            >
              <div
                v-if="!propertyCount"
                ref="no-results"
                class="py-20px"
                :class="[
                  isSideMapActive ? 'tw-col-span-full' : 'col-12',
                  { 'display-none': !showNoResults },
                ]"
              >
                <h3>No results</h3>
                <p class="mb-20px">
                  Try adjusting your search by removing filters or zooming out
                  on the map
                </p>
                <a class="btn btn-bc01 mb-10px mr-20px" :href="currentUrl">
                  Remove all filters
                </a>
                <button
                  ref="zoomOut"
                  type="button"
                  class="btn btn-bc01 mb-10px mr-20px"
                  data-action="zoom-out"
                  @click="$emit('zoom-out', null)"
                >
                  Zoom Out
                </button>
              </div>
              <div
                :class="isSideMapActive ? 'tw-contents' : 'col-12'"
              >
                <div
                  id="properties_listing_container"
                  :class="
                    isSideMapActive
                      ? [
                          'tw-contents',
                          {
                            'updated-property-tile-grid':
                              enableUpdatedPropertyTile,
                            'is-grid-side-map': true,
                          },
                        ]
                      : [
                          'row',
                          {
                            'updated-property-tile-grid':
                              enableUpdatedPropertyTile,
                          },
                        ]
                  "
                  data-hybrid="1"
                >
                  <template v-for="(property, index) in propertiesData">
                    <div
                      :key="
                        property.id
                          ? `property-${property.id}`
                          : `property-${index}`
                      "
                      :data-id="property.id"
                      :class="[
                        {
                          'property-listing-column property-tile-column tw-min-w-0 tw-mb-0':
                            isSideMapActive &&
                            !isGuideBanner(property) &&
                            !mapIsOpen,
                          'tw-col-span-full banner-listing-column':
                            isSideMapActive && isGuideBanner(property),
                          'col-12 col-sm-6 property-listing-column tw-mb-[18px] md:tw-mb-[36px]':
                            !isSideMapActive &&
                            !isGuideBanner(property) &&
                            !mapIsOpen,
                          [propertyColumnClass]:
                            !isSideMapActive &&
                            !isGuideBanner(property) &&
                            !mapIsOpen,
                          'col-12 col-sm-6 property-listing-column mb-20px':
                            !isGuideBanner(property) && mapIsOpen,
                          'col-12 col-sm-12 mb-20px banner-listing-column':
                            !isSideMapActive && isGuideBanner(property),
                          'z-i-3':
                            shortlistNudgeEnabled &&
                            !isSpecialListing(property) &&
                            [3, 5].includes(getPropertyIteration(index)),
                          'disabled-off': reloading,
                        },
                      ]"
                      :data-map-marker="!isSpecialListing(property)"
                      @click="
                        handleShortlistPropertyCardClick(property, $event)
                      "
                    >
                      <PropertyListingFeatured
                        v-if="
                          !isSpecialListing(property) &&
                          property.isFeatured &&
                          !property.lastEnquiredAt &&
                          !enableUpdatedPropertyTile &&
                          enableUpdatedFeaturedProperties
                        "
                        :admin="admin"
                        :map-is-open="mapIsOpen"
                        :admin-first-name="adminFirstName"
                        :bottom-of-the-page="bottomOfThePage"
                        :current-scroll="currentScroll"
                        :iteration="getPropertyIteration(index)"
                        :paginate="paginate"
                        :property="property"
                        :shortlist-active="shortlistActive"
                        :shortlist-read-only="shortlistReadOnly"
                        :enable-shortlist-in-search="enableShortlistInSearch"
                        :shortlist-selection-mode="isEnquirySelectionMode"
                        :is-selected="isShortlistPropertySelected(property.id)"
                        :reloading="reloading"
                        :screen-key="screenKey"
                        :shortlist-nudge-enabled="shortlistNudgeEnabled"
                        :window-height="windowHeight"
                        @selection-change="handleShortlistSelectionChange"
                        @remove-from-shortlist="handleRemoveFromShortlist"
                      />

                      <PropertyListingBackup2024
                        v-else-if="!isSpecialListing(property)"
                        :enable-updated-property-tile="
                          enableUpdatedPropertyTile
                        "
                        :admin="admin"
                        :map-is-open="mapIsOpen"
                        :admin-first-name="adminFirstName"
                        :bottom-of-the-page="bottomOfThePage"
                        :current-scroll="currentScroll"
                        :iteration="getPropertyIteration(index)"
                        :paginate="paginate"
                        :property="property"
                        :shortlist-active="shortlistActive"
                        :shortlist-read-only="shortlistReadOnly"
                        :enable-shortlist-in-search="enableShortlistInSearch"
                        :shortlist-selection-mode="isEnquirySelectionMode"
                        :is-selected="isShortlistPropertySelected(property.id)"
                        :reloading="reloading"
                        :screen-key="screenKey"
                        :shortlist-nudge-enabled="shortlistNudgeEnabled"
                        :window-height="windowHeight"
                        @selection-change="handleShortlistSelectionChange"
                        @remove-from-shortlist="handleRemoveFromShortlist"
                      />
                      <div
                        v-else
                        class="guide_banner shadow min-h-200px bg-white rounded-1 col-12"
                      >
                        <div class="banner_image min-h-100 position-relative">
                          <img
                            v-lazy="cityGuideImage.src"
                            :data-srcset="cityGuideImage.srcset"
                            :alt="currentAreaName || currentCityName"
                            loading="lazy"
                            class="banner_image_src"
                          />
                        </div>
                        <div class="banner_copy direction-ltr">
                          <h3 class="font-size-24px font-weight-700">
                            Ultimate Student Guide to
                            {{ currentAreaName || currentCityName }}
                          </h3>
                          <p>
                            Not sure where to live next year? Get opinions from
                            real students about the area.
                          </p>
                          <a
                            :href="currentAreaGuidePath || currentCityGuidePath"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-uh="view-guide"
                            class="btn btn-bc02-b border-1px border-solid rounded-1 min-w-250px position-relative px-32px font-weight-600"
                          >
                            View Guide
                          </a>
                        </div>
                      </div>
                    </div>

                    <!-- Info Section conditionally displayed based on the index -->
                    <div
                      v-if="shouldShowInfoDiv(index, currentPage)"
                      :key="`info-${index}`"
                      :class="isSideMapActive ? 'tw-col-span-full' : 'col-12'"
                    >
                      <SearchResultsUtilityBanner
                        :city-name="currentCityName"
                        :flush-vertical-margins="isSideMapActive"
                        :variant="
                          showContentsInsuranceBanner
                            ? 'contents-insurance'
                            : 'bills'
                        "
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <!-- - List -->

          <!-- + Side map pane — viewport-sized; explicit px avoids Edge grid stretch -->
          <div
            v-if="isSideMapActive && currentCity"
            class="tw-relative tw-sticky tw-min-w-0 tw-w-full tw-self-start tw-overflow-hidden tw-rounded-2xl tw-border tw-border-[#E5E7EB] tw-bg-white"
            :data-component="isSplitView ? 'split-map-pane' : 'grid-map-pane'"
            :data-testid="isSplitView ? 'split-map-pane' : 'grid-map-pane'"
            :style="gridSideMapPaneStyle"
          >
            <div class="tw-h-full tw-min-h-[300px] tw-w-full">
              <OpenMap
                :key="isSplitView ? 'split-map' : 'grid-map'"
                layout-mode="split"
                :viewport-inset="gridSideMapViewportInset"
                :center="[currentCity.longitude, currentCity.latitude]"
                :enable-shortlist-in-search="enableShortlistInSearch"
                @shortlist-changed="handleMapShortlistChanged"
                @fullscreen-expand="openMarketplaceFullscreenMap"
              />
            </div>
          </div>
          <!-- - Side map pane -->
          </div>

          <!--
            Always a full-width sibling of the card(+map) row — col-12 inside the
            classic Bootstrap row, tw-w-full inside the grid flex-col — so
            pagination stays centered at all breakpoints (including ≤767px).
          -->
          <div
            v-if="paginate"
            ref="hideOnMapCountZero"
            class="hide-on-map-count-zero tw-w-full"
            :class="[
              isSideMapActive ? '' : 'col-12',
              { 'display-none': mapCountZero },
            ]"
          >
            <div v-if="propertiesHasMorePages && !initialPage && false">
              <div
                v-if="!loading"
                class="property_page_view_more max-w-100"
              >
                <div class="col-12 col-sm-6 col-xl-4 px-0 mx-auto">
                  <div
                    class="bg-white h-8px mt-24px mb-16px p-0 position-relative rounded-1"
                  >
                    <span
                      id="meter"
                      class="bg-primary d-block h-100 overflow-hidden position-relative rounded-1"
                      :data-property-count="propertyCount"
                      :style="'width:' + meter + '%'"
                    ></span>
                  </div>
                  <p class="font-size-16px mb-24px">
                    Viewing
                    <span id="viewing">{{ propertiesCount }}</span>
                    of
                    {{
                      propertyCount +
                      ' ' +
                      pluralise('property', propertyCount)
                    }}
                  </p>
                  <button
                    type="button"
                    class="btn btn-bc02 property_page_view_more_button w-100"
                    @click="$emit('show-more-properties', null)"
                  >
                    Show More
                  </button>
                </div>
              </div>
              <div v-else>
                <div
                  class="bg-offwhite infinite-scroll-request max-w-100 property_page_results"
                >
                  <i
                    class="fas fa-spinner fa-lg fa-spin fa-2x text-primary"
                  ></i>
                </div>
              </div>
            </div>
            <div
              v-if="numberOfPages > 1"
              class="tw-mb-[100px] tw-mt-[100px] tw-flex tw-w-full tw-justify-center"
            >
              <nav
                id="property-pagination-wrapper"
                class="align-items-center d-flex flex-direction-column tw-w-full mx-auto min-[576px]:tw-w-[80%] md:tw-px-0 lg:tw-w-auto"
              >
                <CustomPagination
                  v-model="currentPage"
                  :model-value="currentPage"
                  :active-class="'tw-bg-primary-color tw-text-white'"
                  :break-view-class="'break-view-class'"
                  :break-view-link-class="'bg-transparent tw-self-center break-view-link-class tw-text-primary-color'"
                  :break-view-text="'-'"
                  :click-handler="handlePagination"
                  :disabled-class="'!tw-bg-transparent tw-text-[#E5E7EB]'"
                  :margin-pages="1"
                  :next-class="'next-class'"
                  :next-link-class="'tw-text-primary-color align-items-center d-flex tw-font-bold tw-text-[14px] tw-leading-[27px] tw-h-[34px] md:tw-h-[48px] md:tw-w-[48px] hover:tw-text-[#00628F] justify-content-center page-link-class rounded-circle'"
                  :page-class="'page-class'"
                  :page-count="numberOfPages"
                  :page-link-class="'tw-text-primary-color hover:tw-bg-primary-color hover:tw-text-white md:tw-mx-[0] align-items-center d-flex tw-font-bold tw-text-[14px] tw-leading-[27px] tw-w-[34px] tw-h-[34px] tw-min-w-[34px] md:tw-min-w-[48px] md:tw-h-[48px] md:tw-w-[48px] hover:tw-[#00628F] justify-content-center page-link-class rounded-circle'"
                  :page-range="paginationPageRangeBreakPoints"
                  :prev-class="'tw-text-primary-color tw-h-[34px] md:tw-h-[48px] prev-class'"
                  :prev-link-class="'align-items-center d-flex tw-font-bold tw-text-[14px] tw-leading-[27px] tw-h-[34px] md:tw-h-[48px] md:tw-w-[48px] hover:tw-text-[#00628F] justify-content-center page-link-class rounded-circle'"
                  :container-class="'d-flex list-style-none mx-auto mb-20px tw-justify-center tw-gap-[14px] tw-justify-between tw-w-full'"
                  :prev-text="`<i class='far fa-chevron-left tw-text-[14px] tw-mr-[6px]'></i> <span class='tw-text-[15px] md:tw-mr-0'>Prev </span>`"
                  :next-text="`<span class='md:tw-ml-0 tw-inline-block tw-pr-[6px] tw-text-[15px]'>Next </span> <i class='far fa-chevron-left tw-text-[14px] tw-rotate-180'></i>`"
                >
                </CustomPagination>
              </nav>
            </div>
          </div>

          <!-- + Nearby — full-width sibling (same stacking as pagination) -->
          <div
            v-if="displayNearby"
            ref="nearby"
            class="tw-w-full"
            :class="isSideMapActive ? '' : 'col-12'"
          >
            <div class="row">
              <div class="col-12 py-20px">
                <h3
                  class="mb-4px tw-text-trueGray-700 tw-mt-[24px] tw-text-[26px] tw-leading-[38px]"
                >
                  More student accommodation nearby
                </h3>
                <p
                  id="similar-properties-prompt"
                  class="tw-text-[14px] tw-text-sm tw-leading-[21px] tw-text-[#5C6A75]"
                ></p>
              </div>
              <div class="col-12">
                <div
                  id="properties_nearby_listing_container"
                  class="row"
                  :class="{
                    'updated-property-tile-grid': enableUpdatedPropertyTile,
                  }"
                >
                  <div
                    v-for="(property, index) in nearby"
                    :key="`nearby-${property.id}`"
                    class="col-12 col-sm-6 property-listing-column mb-20px"
                    :class="[
                      propertyColumnClass,
                      {
                        'z-i-3':
                          shortlistNudgeEnabled &&
                          [3, 5].includes(index + 1),
                        'disabled-off': reloading,
                      },
                    ]"
                    :data-id="property.id"
                    data-map-marker="true"
                  >
                    <PropertyListingBackup2024
                      :abbreviate="true"
                      :enable-updated-property-tile="
                        enableUpdatedPropertyTile
                      "
                      :admin="admin"
                      :admin-first-name="adminFirstName"
                      class-list="h-100 m-0"
                      :hide-buttons="true"
                      img-classes="max-h-180px-off max-w-100'"
                      :iteration="index + 1"
                      listing="nearby"
                      :property="property"
                      :shortlist-active="shortlistActive"
                      :enable-shortlist-in-search="enableShortlistInSearch"
                      :reloading="reloading"
                      :screen-key="screenKey"
                      :shortlist-nudge-enabled="shortlistNudgeEnabled"
                    >
                    </PropertyListingBackup2024>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- - Nearby -->
        </div>
      </div>
    </div>
    <!-- - Properties ListMap -->

    <!-- Sticky Mobile Map Button - Bottom Fixed Variant (Always Visible) -->
    <template
      v-if="
        (!canShowGridSplitMaps || isMobile) &&
        mapViewPillPositionTest === 'bottom_fixed'
      "
    >
      <!-- - Sticky Mobile Map Button - Bottom Fixed Variant -->
      <button
        v-if="
          !mapIsOpen &&
          canShowMapView &&
          (isMobile || (!canShowGridSplitMaps && isTablet)) &&
          showBottomFixedButton
        "
        ref="mapViewStickyButtonFixed"
        type="button"
        class="tw-fixed tw-bottom-4 tw-left-1/2 tw-z-[9999] tw-flex tw-h-[40px] -tw-translate-x-1/2 tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-full tw-bg-[#0082B8] tw-px-[16px] tw-py-0 tw-text-white tw-shadow-lg"
        @click="toggleMap"
      >
        <img
          alt="View Map"
          class="tw-h-[24px] tw-w-[24px] tw-pr-[4px]"
          :src="cdnAsset('svg/uh-view-map-icon-white.svg')"
        />
        <span class="tw-!text-white tw-text-sm tw-font-semibold">
          Map view
        </span>
      </button>
    </template>
    <template v-else-if="!canShowGridSplitMaps || isMobile">
      <!-- Sticky Mobile Map Button - Control Variant (Original Scroll Logic) -->
      <button
        v-if="
          showStickyButton &&
          canShowMapView &&
          (isMobile || (!canShowGridSplitMaps && isTablet))
        "
        ref="mapViewStickyButton"
        type="button"
        class="tw-fixed tw-left-1/2 tw-z-[9999] tw-mb-4 tw-flex tw-h-[40px] -tw-translate-x-1/2 tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-full tw-bg-[#0082B8] tw-px-[16px] tw-py-0 tw-text-white tw-shadow-lg tw-transition-[opacity,bottom] tw-duration-200 tw-ease-in-out"
        @click="toggleMap"
      >
        <img
          alt="View Map"
          class="tw-h-[24px] tw-w-[24px] tw-pr-[4px]"
          :src="cdnAsset('svg/uh-view-map-icon-white.svg')"
        />
        <span class="tw-!text-white tw-text-sm tw-font-semibold">
          Map view
        </span>
      </button>
    </template>
    <!-- - Sticky Mobile Map Button - Control Variant -->

    <PropertyEnquiryFooter
      v-if="isMultiPropertyEnquiryEnabled"
      :is-selection-mode="isEnquirySelectionMode"
      :selected-count="selectedShortlistPropertyIds.length"
      @start="handleEnquireClick"
      @cancel="cancelEnquirySelection"
      @continue="continueToEnquiry"
    />
  </div>
  <!-- - Properties List -->
</template>
<script>
import axios from 'axios';
import PropertyListingBackup2024 from '@components/PropertyListingBackup2024.vue';
import Pluralize from 'pluralize';
import CustomPagination from '@components/CustomPagination.vue';
import UpcomingPropertiesBanner from '@components/Property/UpcomingPropertiesBanner.vue';
import SearchResultsUtilityBanner from '@components/properties-search/components/SearchResultsUtilityBanner.vue';
import PropertyEnquiryFooter from '@components/properties-search/components/PropertyEnquiryFooter.vue';
import OpenMap from '@components/properties-search/components/OpenMap.vue';
import SpareRoomsNudge from '@components/properties-search/components/SpareRoomsNudge.vue';
import { useMapStore } from '@js/stores/map/mapStore';
import PropertyListingFeatured from '@components/PropertyListingFeatured.vue';
import { getCurrentPath } from '@js/utilities';
import PropertyTypeFiltersExtension from '@components/Property/MobileSearchFilters/PropertyTypeFiltersExtension.vue';
import PropertyTypeFiltersTrigger from '@components/Property/MobileSearchFilters/PropertyTypeFiltersTrigger.vue';
import { useStudentStore } from '@js/stores/student/student';
import { sendLaunchDarklyMetric } from '@js/launchdarkly-track';
import { syncEnquiryModalShortlistSummary } from '@js/syncEnquiryModalShortlistSummary';

export default {
  components: {
    SearchResultsUtilityBanner,
    PropertyEnquiryFooter,
    PropertyListingFeatured,
    OpenMap,
    CustomPagination,
    PropertyTypeFiltersExtension,
    PropertyTypeFiltersTrigger,
    PropertyListingBackup2024,
    UpcomingPropertiesBanner,
    SpareRoomsNudge,
  },

  props: {
    shortlistActive: {
      default: false,
      type: Boolean,
    },
    shortlistReadOnly: {
      default: false,
      type: Boolean,
    },
    shortlistTitle: {
      default: '',
      type: String,
    },
    shortlistResultsLoaded: {
      default: true,
      type: Boolean,
    },
    enableShortlistInSearch: {
      default: false,
      type: Boolean,
    },
    enableNewMap: {
      default: false,
      type: Boolean,
    },
    enableMarketplaceGridSplitMaps: {
      default: false,
      type: Boolean,
    },
    desktopPropertyFiltersOpen: {
      default: false,
      type: Boolean,
    },
    selectedView: {
      default: 'list_view',
      type: String,
    },
    enableUpdatedFeaturedProperties: {
      default: false,
      type: Boolean,
    },
    enableUpdatedPropertyTile: {
      default: false,
      type: Boolean,
    },
    showUpcomingPropertiesBanner: {
      default: false,
      type: Boolean,
    },
    showContentsInsuranceBanner: {
      default: false,
      type: Boolean,
    },
    admin: {
      default: false,
      type: Boolean,
    },
    areas: {
      default: function () {
        return [];
      },
      type: Array,
    },
    adminFirstName: {
      default: null,
      type: String,
    },
    bottomOfThePage: {
      default: window.innerHeight,
      type: Number,
    },
    cities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    currentAreaSlug: {
      default: null,
      type: String,
    },
    currentCitySlug: {
      default: null,
      type: String,
    },
    currentScroll: {
      default: 0,
      type: Number,
    },
    currentSort: {
      default: null,
      type: String,
    },
    currentSortTitle: {
      default: null,
      type: String,
    },
    currentSortTitleShort: {
      default: null,
      type: String,
    },
    currentType: {
      default: null,
      type: [String, Array],
    },
    enablePrivateHallsFilter: {
      default: false,
      type: Boolean,
    },
    enableSpareRoomFunctionality: {
      default: false,
      type: Boolean,
    },
    enablePropertyTypeFilters: {
      default: false,
      type: Boolean,
    },
    currentUrl: {
      default: null,
      type: String,
    },
    displayNearby: {
      default: false,
      type: Boolean,
    },
    hideNearby: {
      default: false,
      type: Boolean,
    },
    initialPage: {
      default: null,
      type: [Number, String],
    },
    loading: {
      default: false,
      type: Boolean,
    },
    mapCountZero: {
      default: false,
      type: Boolean,
    },
    mapMoveUp: {
      default: false,
      type: Boolean,
    },
    nearby: {
      default: function () {
        return [];
      },
      type: Array,
    },
    numberOfPages: {
      default: 1,
      type: Number,
    },
    page: {
      default: null,
      type: [Number, String],
    },
    paginate: {
      default: 0,
      type: Number,
    },
    properties: {
      default: function () {
        return [];
      },
      type: Array,
    },
    propertiesCount: {
      default: 0,
      type: Number,
    },
    propertiesHasMorePages: {
      default: false,
      type: Boolean,
    },
    propertiesSliderSlides: {
      default: null,
      type: Array,
    },
    propertyCount: {
      default: 0,
      type: Number,
    },
    reloading: {
      default: false,
      type: Boolean,
    },
    screenKey: {
      default: null,
      type: String,
    },
    shortlistNudgeEnabled: {
      default: false,
      type: Boolean,
    },
    showNearby: {
      default: 0,
      type: Number,
    },
    showNoResults: {
      default: false,
      type: Boolean,
    },
    sortOptions: {
      default: function () {
        return {};
      },
      type: Object,
    },
    title: {
      default: null,
      type: String,
    },
    titleResultsCount: {
      default: null,
      type: [Number, String],
    },
    windowHeight: {
      default: window.innerHeight,
      type: Number,
    },
    zoomLevel: {
      default: 12,
      type: Number,
    },
    enquiredProperties: {
      default: false,
      type: Boolean,
    },
    mapViewPillPositionTest: {
      default: 'control',
      type: String,
    },
    showSpareRoomsOneBedNudge: {
      default: false,
      type: Boolean,
    },
    spareRoomsOneBedOverlayCount: {
      default: 0,
      type: Number,
    },
    spareRoomsOneBedOverlayApplyUrl: {
      default: '',
      type: String,
    },
  },

  data() {
    return {
      windowWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      /** Sticky filters bar height — grows when property-type panel is open. */
      filtersBarHeight: 120,
      filtersBarResizeObserver: null,
      filtersBarMeasureTimer: null,
      showStickyButton: false,
      showBottomFixedButton: true,
      paginationVisible: false,
      paginationObserver: null,
      mapHasBeenOpened: false,
      propertyTypeFiltersExpanded: true,
      shareShortlistLabel: 'Share Shortlist',
      sharingShortlist: false,
      localProperties: [...this.properties],
      studentStore: useStudentStore(),
      isEnquirySelectionMode: false,
      selectedEnquiryPropertyRecords: [],
      pendingShortlistEnquiryClick: false,
    };
  },

  computed: {
    propertyColumnClass() {
      return this.enableUpdatedPropertyTile
        ? 'col-lg-4 property-tile-column'
        : 'col-xl-4';
    },
    mapStore() {
      return useMapStore();
    },
    mapIsOpen() {
      return this.mapStore.mapIsOpen;
    },
    canShowMapView() {
      if (this.shortlistActive) return false;

      const currentPath = this.getCurrentPath();
      return (
        currentPath &&
        currentPath !== '/student-accommodation' &&
        this.enableNewMap
      );
    },
    canShowGridSplitMaps() {
      return Boolean(
        this.canShowMapView && this.enableMarketplaceGridSplitMaps
      );
    },
    isSplitView() {
      return (
        this.canShowGridSplitMaps &&
        (this.selectedView === 'split_view' || this.selectedView === 'split')
      );
    },
    isGridView() {
      return (
        this.canShowGridSplitMaps &&
        (this.selectedView === 'grid_view' || this.selectedView === 'grid')
      );
    },
    /**
     * Desktop/tablet Grid or Split layout with a side map. Mobile falls back
     * to the normal list + floating Map view button.
     */
    isSideMapActive() {
      return (this.isGridView || this.isSplitView) && this.windowWidth >= 768;
    },
    /**
     * Card columns beside the map.
     * Grid: >=1200 → 3, >=900 → 2, else (md) → 1. The map is one equal track.
     * Split: >=1200 → 2, else (md) → 1. The map track is at least as wide as
     * the cards pane, so it takes more of the row than Grid.
     */
    gridSideMapCardColumns() {
      if (this.isSplitView) {
        return this.windowWidth >= 1200 ? 2 : 1;
      }

      if (this.windowWidth >= 1200) {
        return 3;
      }
      if (this.windowWidth >= 900) {
        return 2;
      }
      return 1;
    },
    gridSideMapListPaneClass() {
      if (this.isSideMapActive) {
        // Subgrid shares the parent card tracks and column gaps.
        return 'tw-grid tw-min-w-0 tw-gap-y-9';
      }

      return ['col-12', { 'col-lg-6 d-none d-lg-block': this.mapIsOpen }];
    },
    /**
     * Grid gives every card column and the map an equal track.
     * Split gives the map a track equal to the whole cards pane (50% at
     * two-up) or 1.5× the single card (60% at one-up).
     * The list pane uses subgrid so card gutters match the gap before the
     * map (no nested grid double-counting gaps, no Bootstrap row overflow).
     */
    gridSideMapRowStyle() {
      const cols = this.gridSideMapCardColumns;

      if (this.isSplitView) {
        const mapTrack = cols === 1 ? '1.5fr' : `${cols}fr`;

        return {
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr)) minmax(0, ${mapTrack})`,
        };
      }

      return {
        gridTemplateColumns: `repeat(${cols + 1}, minmax(0, 1fr))`,
      };
    },
    gridSideMapListPaneStyle() {
      const cols = this.gridSideMapCardColumns;

      return {
        gridColumn: `span ${cols}`,
        gridTemplateColumns: 'subgrid',
      };
    },
    /**
     * Gap between sticky filters and the map top — kept constant whether the
     * property-type panel is open or closed.
     */
    gridSideMapFiltersGap() {
      return 16;
    },
    /**
     * Sticky top offset: live filters bar height + constant gap.
     */
    gridSideMapStickyTop() {
      return this.filtersBarHeight + this.gridSideMapFiltersGap;
    },
    /**
     * Space above the map (sticky top) plus a small bottom inset.
     */
    gridSideMapViewportInset() {
      return this.gridSideMapStickyTop + 20;
    },
    /**
     * Pixel height from the viewport so Edge cannot stretch the sticky map
     * pane to the full cards-column grid track.
     */
    gridSideMapPaneStyle() {
      const height = Math.max(
        this.viewportHeight - this.gridSideMapViewportInset,
        300
      );

      return {
        top: `${this.gridSideMapStickyTop}px`,
        height: `${height}px`,
        maxHeight: `${height}px`,
      };
    },
    updatedTileGridColumns() {
      const width = this.windowWidth;

      if (this.isSideMapActive) {
        return this.gridSideMapCardColumns;
      }

      if (width >= 1343) {
        return 4;
      }
      if (width >= 992) {
        return 3;
      }
      if (width >= 576) {
        return 2;
      }

      return 1;
    },
    /**
    The banner is full width, so it breaks whichever row it lands in. Round the
    four card target the design asks for up to the end of a row, which lands it
    after 4 cards four-up, 6 three-up and 4 two-up.
     */
    infoDivIndex() {
      const columns = this.updatedTileGridColumns;

      return Math.ceil(4 / columns) * columns - 1;
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    isTablet() {
      return this.windowWidth < 1024;
    },
    paginationPageRangeBreakPoints() {
      const width = this.windowWidth;

      if (this.mapIsOpen && width >= 992) {
        return 3;
      } else if (width >= 992) {
        return 9;
      } else if (width >= 768) {
        return 5;
      } else {
        return 2;
      }
    },
    currentArea() {
      var scope = this;
      return scope.currentAreaSlug
        ? this.areas.filter(function (area) {
            return area.slug === scope.currentAreaSlug;
          })[0]
        : null;
    },
    currentAreaName() {
      return this.currentArea ? this.currentArea.name : null;
    },
    currentAreaGuidePath() {
      return this.currentArea ? this.currentArea.guide_path : null;
    },
    currentCity() {
      var scope = this;
      return scope.currentCitySlug
        ? this.cities.filter(function (city) {
            return city.id === scope.currentCitySlug;
          })[0]
        : null;
    },
    currentCityName() {
      return this.currentCity ? this.currentCity.name : null;
    },
    currentCityGuidePath() {
      return this.currentCity ? this.currentCity.guide_path : null;
    },
    cityGuideImage() {
      const image = { src: '', srcset: '' };

      if (this.currentArea && this.currentArea.src) {
        image.src = this.currentArea.src.homepage.url;
        image.srcset = this.currentArea.src.homepage.srcset;
      }

      if (this.currentCity && this.currentCity.src) {
        image.src = this.currentCity.src.homepage.url;
        image.srcset = this.currentCity.src.homepage.srcset;
      }

      return image;
    },
    currentEndCount() {
      return this.propertyCount
        ? Math.min(this.page * this.paginate, this.propertyCount)
        : null;
    },
    currentMaxPropertyCount() {
      if (this.propertyCount < 100) return this.propertyCount;

      const roundedTo = this.propertyCount > 1000 ? 1000 : 100;
      const nearestNumber =
        Math.floor(this.propertyCount / roundedTo) * roundedTo;

      return `${nearestNumber}+`;
    },
    searchPromoOffset() {
      return 0;
    },
    currentPage: {
      get: function () {
        return this.page;
      },
      set: function (value) {
        this.$emit('set-page', value);
      },
    },
    meter() {
      return this.propertyCount
        ? Math.min(
            Math.ceil(
              (((this.page ? this.page : 1) * this.paginate) /
                this.propertyCount) *
                100
            ),
            100
          )
        : 0;
    },
    propertiesData() {
      return this.localProperties;
    },
    isMultiPropertyEnquiryEnabled() {
      return (
        this.enableShortlistInSearch &&
        this.shortlistActive &&
        this.propertiesData.length > 0
      );
    },
    selectedShortlistPropertyIds() {
      return Array.isArray(this.studentStore.selectedPropertyIds)
        ? this.studentStore.selectedPropertyIds.map(String)
        : [];
    },
    selectedEnquiryProperties() {
      return this.selectedShortlistPropertyIds
        .map((id) =>
          this.selectedEnquiryPropertyRecords.find(
            (property) => String(property?.id) === id
          )
        )
        .filter(Boolean);
    },
    displayUpcomingPropertiesBanner() {
      return (
        this.showUpcomingPropertiesBanner &&
        this.currentCity &&
        this.currentCity.country === 'GB-SCT'
      );
    },
  },

  watch: {
    properties: {
      handler(properties) {
        this.localProperties = [...properties];
      },
    },
    mapIsOpen: function (newVal) {
      if (newVal) {
        this.mapOpened();
      } else {
        this.mapClosed();
      }
    },
    isMultiPropertyEnquiryEnabled() {
      this.removePreVueEnquiryFooterIfResolved();
    },
    reloading() {
      this.removePreVueEnquiryFooterIfResolved();
    },
    shortlistResultsLoaded(isLoaded) {
      if (isLoaded && this.pendingShortlistEnquiryClick) {
        this.pendingShortlistEnquiryClick = false;
        this.$nextTick(() => this.handleEnquireClick());
      }
    },
    shortlistActive() {
      this.removePreVueEnquiryFooterIfResolved();
    },
    isSideMapActive(isActive) {
      if (isActive) {
        this.$nextTick(() => this.observeFiltersBarHeight());
      } else {
        this.teardownFiltersBarObserver();
      }
    },
    desktopPropertyFiltersOpen() {
      if (!this.isSideMapActive) {
        return;
      }

      // Remeasure during and after the 300ms panel height animation.
      this.scheduleFiltersBarRemeasure();
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener(
      'shortlist-enquiry-selection-requested',
      this.handleEnquireClick
    );
    if (window.pendingShortlistEnquirySelection) {
      window.pendingShortlistEnquirySelection = false;
      this.handleEnquireClick();
    }
    this.removePreVueEnquiryFooterIfResolved();
    // Initialize button visibility based on variant
    this.handleScroll();
    if (this.isSideMapActive) {
      this.observeFiltersBarHeight();
    }
  },
  beforeUnmount() {
    this.teardownFiltersBarObserver();
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener(
      'shortlist-enquiry-selection-requested',
      this.handleEnquireClick
    );
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener(
      'shortlist-enquiry-selection-requested',
      this.handleEnquireClick
    );
  },
  methods: {
    handleRemoveFromShortlist(propertyId) {
      this.$emit('remove-from-shortlist', propertyId);
    },
    handleEnquireClick() {
      if (this.shortlistActive && !this.shortlistResultsLoaded) {
        this.pendingShortlistEnquiryClick = true;
        return;
      }

      if (!this.isMultiPropertyEnquiryEnabled) return;

      if (
        this.shortlistActive &&
        this.propertyCount === 1 &&
        this.propertiesData.length === 1
      ) {
        this.openEnquiryModal(this.propertiesData, [this.propertiesData[0].id]);
        return;
      }

      this.startEnquirySelection();
    },
    openEnquiryModal(enquiryProperties, propertyIds) {
      if (!propertyIds.length) return;

      syncEnquiryModalShortlistSummary(enquiryProperties);

      const propertyIdsInput = document.getElementById(
        'enquiry_shortlist_property_ids'
      );
      if (propertyIdsInput instanceof HTMLInputElement) {
        propertyIdsInput.value = propertyIds.join(',');
      }

      const shortlistHashInput = document.getElementById('shortlist_hash');
      const shortlistHash = new URLSearchParams(window.location.search).get(
        'shortlistHash'
      );
      if (shortlistHashInput instanceof HTMLInputElement) {
        shortlistHashInput.value = shortlistHash || '';
      }

      const enquiryModalToggle = document.getElementById('enquiry_modal');
      if (enquiryModalToggle instanceof HTMLInputElement) {
        enquiryModalToggle.checked = true;
        // Programmatic changes don't fire `change`; the modal's body scroll lock listens for it.
        enquiryModalToggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    removePreVueEnquiryFooterIfResolved() {
      if (
        this.isMultiPropertyEnquiryEnabled ||
        !this.shortlistActive ||
        !this.reloading
      ) {
        document.querySelector('[data-pre-vue-enquiry-footer]')?.remove();
      }
    },
    handleMapShortlistChanged(property) {
      const propertyId = String(property?.id ?? '');
      if (!propertyId) return;

      const isShortlisted = Boolean(property.isShortlisted);
      this.localProperties = this.localProperties.map((item) =>
        String(item.id) === propertyId
          ? { ...item, shortlisted: isShortlisted }
          : item
      );
    },
    getCurrentPath,
    async shareShortlist() {
      if (this.sharingShortlist) return;

      this.sharingShortlist = true;

      try {
        const shareUrl = await useStudentStore().createShortlistShareLink();
        if (!shareUrl) {
          this.shareShortlistLabel = 'Cannot share shortlist';
          return;
        }

        if (navigator.share) {
          await navigator.share({
            title: 'UniHomes',
            text: `View my shortlist using this link:\n${shareUrl}`,
            url: shareUrl,
          });
          this.shareShortlistLabel = 'Shared';
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          this.shareShortlistLabel = 'Link copied';
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          this.shareShortlistLabel = 'Cannot share shortlist';
        }
      } finally {
        this.sharingShortlist = false;
        window.setTimeout(() => {
          this.shareShortlistLabel = 'Share Shortlist';
        }, 3000);
      }
    },
    startEnquirySelection() {
      this.studentStore.clearShortlistSelection();
      this.selectedEnquiryPropertyRecords = [];
      this.isEnquirySelectionMode = true;
    },
    cancelEnquirySelection() {
      this.studentStore.clearShortlistSelection();
      this.selectedEnquiryPropertyRecords = [];
      this.isEnquirySelectionMode = false;
    },
    isShortlistPropertySelected(propertyId) {
      return this.selectedShortlistPropertyIds.includes(String(propertyId));
    },
    handleShortlistSelectionChange({ id, checked }) {
      if (checked) {
        this.studentStore.selectShortlistProperty(id);
        this.rememberSelectedEnquiryProperty(id);
      } else {
        this.studentStore.deselectShortlistProperty(id);
        this.selectedEnquiryPropertyRecords =
          this.selectedEnquiryPropertyRecords.filter(
            (property) => String(property?.id) !== String(id)
          );
      }
    },
    rememberSelectedEnquiryProperty(propertyId) {
      const property = this.propertiesData.find(
        (item) => String(item?.id) === String(propertyId)
      );

      if (
        property &&
        !this.selectedEnquiryPropertyRecords.some(
          (item) => String(item?.id) === String(propertyId)
        )
      ) {
        this.selectedEnquiryPropertyRecords.push(property);
      }
    },
    handleShortlistPropertyCardClick(property, event) {
      if (!this.isMultiPropertyEnquiryEnabled || !this.isEnquirySelectionMode) {
        return;
      }

      if (!(event.target instanceof Element)) return;

      if (
        event.target.closest(
          '[data-shortlist-selection-control], [data-tile-click-ignore], button, input, label'
        )
      ) {
        return;
      }

      event.preventDefault();
      const propertyId = String(property?.id ?? '');
      if (!propertyId) return;

      if (this.isShortlistPropertySelected(propertyId)) {
        this.handleShortlistSelectionChange({ id: propertyId, checked: false });
      } else {
        this.handleShortlistSelectionChange({ id: propertyId, checked: true });
      }
    },
    continueToEnquiry() {
      if (!this.selectedShortlistPropertyIds.length) return;
      this.openEnquiryModal(
        this.selectedEnquiryProperties,
        this.selectedShortlistPropertyIds
      );
    },
    getPropertyIteration(index) {
      return index + 1 - this.searchPromoOffset;
    },
    isGuideBanner(property) {
      return property?.id === 'guide-banner';
    },
    isSpecialListing(property) {
      return this.isGuideBanner(property);
    },
    toggleMap() {
      this.$emit('toggle-map-is-open');
    },
    setMarketplaceView(view) {
      this.$emit('set-selected-view', view);
    },
    openMarketplaceFullscreenMap() {
      this.$emit('set-selected-view', 'map_view');
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.viewportHeight = window.innerHeight;
      this.updateFiltersBarHeight();
    },
    getStickyFiltersBarElement() {
      return (
        document.querySelector('[data-testid="sticky-filters-bar"]') ||
        document.querySelector('#properties_filter')
      );
    },
    updateFiltersBarHeight() {
      const filtersBar = this.getStickyFiltersBarElement();
      if (!filtersBar) {
        return;
      }

      const nextHeight = Math.round(filtersBar.getBoundingClientRect().height);
      if (nextHeight > 0) {
        this.filtersBarHeight = nextHeight;
      }
    },
    scheduleFiltersBarRemeasure() {
      this.updateFiltersBarHeight();
      this.$nextTick(() => {
        this.updateFiltersBarHeight();
        requestAnimationFrame(() => this.updateFiltersBarHeight());
      });

      if (this.filtersBarMeasureTimer) {
        clearTimeout(this.filtersBarMeasureTimer);
      }

      // Panel height animation is 300ms — catch the settled open/closed size.
      this.filtersBarMeasureTimer = setTimeout(() => {
        this.updateFiltersBarHeight();
        this.filtersBarMeasureTimer = null;
      }, 320);
    },
    observeFiltersBarHeight() {
      this.scheduleFiltersBarRemeasure();

      if (this.filtersBarResizeObserver || typeof ResizeObserver === 'undefined') {
        return;
      }

      const filtersBar = this.getStickyFiltersBarElement();
      if (!filtersBar) {
        return;
      }

      this.filtersBarResizeObserver = new ResizeObserver(() => {
        this.updateFiltersBarHeight();
      });
      this.filtersBarResizeObserver.observe(filtersBar);
    },
    teardownFiltersBarObserver() {
      if (this.filtersBarMeasureTimer) {
        clearTimeout(this.filtersBarMeasureTimer);
        this.filtersBarMeasureTimer = null;
      }

      if (this.filtersBarResizeObserver) {
        this.filtersBarResizeObserver.disconnect();
        this.filtersBarResizeObserver = null;
      }
    },
    handleScroll() {
      // Handle bottom_fixed variant - hide when scrolling past list container
      if (this.mapViewPillPositionTest === 'bottom_fixed') {
        if (this.mapIsOpen) {
          this.showBottomFixedButton = false;
          return;
        }

        // Use permanent fixture - list container always exists
        const listContainer = this.$refs.listContainer;
        let containerDistanceFromTop = 0;

        if (listContainer) {
          const rect = listContainer.getBoundingClientRect();
          // Use bottom of container as boundary
          containerDistanceFromTop = rect.bottom;
        }

        // Hide button when scrolling past the bottom of the list container
        this.showBottomFixedButton =
          containerDistanceFromTop > window.innerHeight;
        return;
      }

      // Handle control variant - original scroll logic
      if (this.mapViewPillPositionTest !== 'control') {
        return;
      }

      if (this.mapIsOpen) {
        this.showStickyButton = false;
        return;
      }

      this.showStickyButton = true;

      // hide sticky button if at the top of the page and slide it up as user scrolls down
      const stickyButton = this.$refs.mapViewStickyButton;

      if (stickyButton) {
        const stickyButtonRect = stickyButton.getBoundingClientRect();
        const distanceFromTop = window.scrollY;
        const slideThreshold = stickyButtonRect.height + 18; // distance from the bottom including 18px margin (tw-mb-4)

        // Fade in as it slides up into view from top
        const baseOpacity = Math.max(
          0,
          Math.min(1, distanceFromTop / Math.max(1, slideThreshold))
        );

        if (distanceFromTop < slideThreshold) {
          stickyButton.style.bottom = distanceFromTop - slideThreshold + 'px';
        } else {
          stickyButton.style.bottom =
            stickyButtonRect.height !== 0 ? 0 : stickyButton.style.bottom;
        }

        // hide sticky button if user scrolls past the search results/list container
        // Use permanent fixture - list container always exists
        const listContainer = this.$refs.listContainer;
        let containerDistanceFromTop = 0;

        if (listContainer) {
          const rect = listContainer.getBoundingClientRect();
          // Use bottom of container as boundary
          containerDistanceFromTop = rect.bottom;
        }

        let fadeOutOpacity = 1;
        if (containerDistanceFromTop <= window.innerHeight) {
          fadeOutOpacity = Math.max(
            0,
            Math.min(1, containerDistanceFromTop - window.innerHeight)
          );
        }

        stickyButton.style.opacity = Math.max(
          0,
          Math.min(1, Math.min(baseOpacity, fadeOutOpacity))
        );

        this.showStickyButton = containerDistanceFromTop > window.innerHeight;
      }
    },
    shouldShowInfoDiv(index, currentPage) {
      const isMobile = this.windowWidth < 768;

      if (currentPage % 2 === 0) {
        return false;
      }
      if (this.enableUpdatedPropertyTile) {
        return index === this.infoDivIndex;
      }

      // Only show once on the first page
      if (currentPage === 1) {
        if (isMobile) {
          return index === 0;
        } else return index === 5;
      } else {
        if (isMobile) {
          return index === 4;
        }
        return index === 5;
      }
    },
    handlePagination(page) {
      this.$emit('set-page', page);
    },
    mapOpened() {
      // Prevent body scrolling when map is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      // Track when map is first opened for A/B test
      if (!this.mapHasBeenOpened) {
        this.mapHasBeenOpened = true;
        this.sendLaunchDarklyEvent('map_view_opened', {
          test_variation: this.mapViewPillPositionTest,
          button_position:
            this.mapViewPillPositionTest === 'bottom_fixed'
              ? 'bottom_fixed'
              : 'top',
        });
      }
    },
    mapClosed() {
      // Restore body scrolling when map is closed
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      // Trigger scroll handler to update button visibility
      this.handleScroll();
    },
    pluralise(string, number, boolean) {
      return Pluralize(string, number, boolean);
    },
    ucfirst(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    sendLaunchDarklyEvent(eventName, data = {}) {
      sendLaunchDarklyMetric(
        eventName,
        {
          ...data,
          test_name: 'map_view_pill_position_test',
        },
        { transport: 'axios' }
      );
    },
  },
};
</script>
